/**
 * Canvas-based utility for creating composite before/after renovation images
 * for social media sharing. Supports side-by-side (landscape) and stacked
 * (portrait) layouts with Plum branding.
 */

const PLUM_DARKEST = '#0D0A14';
const PLUM_GOLD = '#D4A843';
const BRANDING_HEIGHT = 44;
const LABEL_FONT = 'bold 18px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const BRANDING_FONT = '14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const BRANDING_BOLD_FONT = 'bold 14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

// OG-optimized dimensions
const SIDE_BY_SIDE_WIDTH = 1200;
const SIDE_BY_SIDE_HEIGHT = 630;
const STACKED_WIDTH = 630;
const STACKED_HEIGHT = 1200;

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
    img.src = url;
  });
}

function drawImageFitted(ctx, img, x, y, w, h) {
  const scale = Math.min(w / img.width, h / img.height);
  const drawW = img.width * scale;
  const drawH = img.height * scale;
  const drawX = x + (w - drawW) / 2;
  const drawY = y + (h - drawH) / 2;
  ctx.drawImage(img, drawX, drawY, drawW, drawH);
}

function drawLabel(ctx, text, x, y, w) {
  ctx.font = LABEL_FONT;
  const metrics = ctx.measureText(text);
  const paddingX = 14;
  const pillW = metrics.width + paddingX * 2;
  const pillH = 28;
  const pillX = x + (w - pillW) / 2;
  const pillY = y + 16;

  ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
  ctx.beginPath();
  ctx.roundRect(pillX, pillY, pillW, pillH, pillH / 2);
  ctx.fill();

  ctx.fillStyle = '#FFFFFF';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, pillX + pillW / 2, pillY + pillH / 2);
}

function drawBranding(ctx, canvasWidth, canvasHeight) {
  const barY = canvasHeight - BRANDING_HEIGHT;

  // Gradient background
  const grad = ctx.createLinearGradient(0, barY, canvasWidth, barY);
  grad.addColorStop(0, '#1A1225');
  grad.addColorStop(1, '#2A1F35');
  ctx.fillStyle = grad;
  ctx.fillRect(0, barY, canvasWidth, BRANDING_HEIGHT);

  // Top border accent
  ctx.fillStyle = PLUM_GOLD;
  ctx.fillRect(0, barY, canvasWidth, 2);

  // Branding text
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const centerY = barY + BRANDING_HEIGHT / 2 + 1;

  ctx.font = BRANDING_BOLD_FONT;
  ctx.fillStyle = PLUM_GOLD;
  const plumText = 'Made with Plum';
  const plumMetrics = ctx.measureText(plumText);

  ctx.font = BRANDING_FONT;
  ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
  const urlText = '  \u2022  plum-app.com';
  const urlMetrics = ctx.measureText(urlText);

  const totalWidth = plumMetrics.width + urlMetrics.width;
  const startX = (canvasWidth - totalWidth) / 2;

  ctx.font = BRANDING_BOLD_FONT;
  ctx.fillStyle = PLUM_GOLD;
  ctx.textAlign = 'left';
  ctx.fillText(plumText, startX, centerY);

  ctx.font = BRANDING_FONT;
  ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
  ctx.fillText(urlText, startX + plumMetrics.width, centerY);
}

/**
 * Create a composite before/after image.
 * @param {string} beforeUrl - URL of the before image
 * @param {string} afterUrl - URL of the after image
 * @param {Object} options
 * @param {'side-by-side'|'stacked'} options.layout - Layout mode
 * @returns {Promise<{blob: Blob, dataUrl: string}>}
 */
export async function createBeforeAfterComposite(beforeUrl, afterUrl, { layout = 'side-by-side' } = {}) {
  const [beforeImg, afterImg] = await Promise.all([
    loadImage(beforeUrl),
    loadImage(afterUrl),
  ]);

  const isStacked = layout === 'stacked';
  const canvasWidth = isStacked ? STACKED_WIDTH : SIDE_BY_SIDE_WIDTH;
  const canvasHeight = isStacked ? STACKED_HEIGHT : SIDE_BY_SIDE_HEIGHT;

  const canvas = document.createElement('canvas');
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;
  const ctx = canvas.getContext('2d');

  // Background
  ctx.fillStyle = PLUM_DARKEST;
  ctx.fillRect(0, 0, canvasWidth, canvasHeight);

  const contentHeight = canvasHeight - BRANDING_HEIGHT;
  const dividerSize = 3;

  if (isStacked) {
    // Stacked: top = before, bottom = after
    const halfH = (contentHeight - dividerSize) / 2;
    drawImageFitted(ctx, beforeImg, 0, 0, canvasWidth, halfH);
    drawImageFitted(ctx, afterImg, 0, halfH + dividerSize, canvasWidth, halfH);

    // Divider
    ctx.fillStyle = PLUM_GOLD;
    ctx.fillRect(0, halfH, canvasWidth, dividerSize);

    // Labels
    drawLabel(ctx, 'BEFORE', 0, 0, canvasWidth);
    drawLabel(ctx, 'AFTER', 0, halfH + dividerSize, canvasWidth);
  } else {
    // Side-by-side: left = before, right = after
    const halfW = (canvasWidth - dividerSize) / 2;
    drawImageFitted(ctx, beforeImg, 0, 0, halfW, contentHeight);
    drawImageFitted(ctx, afterImg, halfW + dividerSize, 0, halfW, contentHeight);

    // Divider
    ctx.fillStyle = PLUM_GOLD;
    ctx.fillRect(halfW, 0, dividerSize, contentHeight);

    // Labels
    drawLabel(ctx, 'BEFORE', 0, 0, halfW);
    drawLabel(ctx, 'AFTER', halfW + dividerSize, 0, halfW);
  }

  // Branding
  drawBranding(ctx, canvasWidth, canvasHeight);

  const dataUrl = canvas.toDataURL('image/png');
  const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));

  return { blob, dataUrl };
}

/**
 * Create a single image with Plum branding overlay (when no before photo is selected).
 * @param {string} imageUrl - URL of the image
 * @param {Object} options
 * @param {'side-by-side'|'stacked'} options.layout - Layout mode (determines dimensions)
 * @returns {Promise<{blob: Blob, dataUrl: string}>}
 */
export async function createSingleImageComposite(imageUrl, { layout = 'side-by-side' } = {}) {
  const img = await loadImage(imageUrl);

  const isStacked = layout === 'stacked';
  const canvasWidth = isStacked ? STACKED_WIDTH : SIDE_BY_SIDE_WIDTH;
  const canvasHeight = isStacked ? STACKED_HEIGHT : SIDE_BY_SIDE_HEIGHT;

  const canvas = document.createElement('canvas');
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;
  const ctx = canvas.getContext('2d');

  // Background
  ctx.fillStyle = PLUM_DARKEST;
  ctx.fillRect(0, 0, canvasWidth, canvasHeight);

  const contentHeight = canvasHeight - BRANDING_HEIGHT;
  drawImageFitted(ctx, img, 0, 0, canvasWidth, contentHeight);

  // Branding
  drawBranding(ctx, canvasWidth, canvasHeight);

  const dataUrl = canvas.toDataURL('image/png');
  const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));

  return { blob, dataUrl };
}
